import { useLayoutEffect, useSyncExternalStore } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api } from '../../services/api';

export interface Station {
  key: string;
  name: string;
  items: string[];
}
export interface StaffPerson {
  userId?: string;
  contactId?: string;
  name?: string;
  email?: string;
}
export interface Shift {
  id: string;
  date: string;
  station: string;
  sessionId?: string | null;
  start?: string | null;
  end?: string | null;
  notes?: string | null;
  items?: string[];
  person: StaffPerson;
  personKey: string;
  personName: string;
}
export interface DayOfSession {
  id: string;
  day: string;
  startTime: string;
  endTime: string | null;
  title: string;
  room: string | null;
}
export interface Leg {
  date?: string;
  time?: string;
  via?: string;
  mode?: string;
  ref?: string;
}
export interface Travel {
  arrive?: Leg;
  depart?: Leg;
}
export interface DayOfPerson {
  inviteeId: string;
  name: string;
  email: string | null;
  role: string | null;
  organization: string | null;
  audienceKey: string | null;
  days: string[];
  group: string | null;
  parking: boolean;
  travelMode: string | null;
  travel: Travel | null;
  accessibility: string | null;
  dietaryTags: string[];
}
export interface Garage {
  key: string;
  name: string;
  instructions?: string | null;
}
export interface ParkingCode {
  id: string;
  garage: string;
  date: string;
  code: string;
  inviteeId: string | null;
  sentAt?: string | null;
  void?: boolean;
  name: string | null;
}
export interface GarageDay {
  key: string;
  name: string;
  total: number;
  assigned: number;
  left: number;
}
export interface ParkingCounts {
  perDay: { date: string; garages: GarageDay[]; needed: number }[];
  requested: number;
  withCodes: number;
  sent: number;
  left: number;
}
export interface BoardPerson {
  key: string;
  name: string;
  date: string;
  time: string | null;
  via: string;
  mode: string | null;
  ref: string | null;
}
export interface BoardDay {
  date: string;
  count: number;
  places: { via: string; people: BoardPerson[]; rides: string[][] }[];
}
export interface Board {
  arrivals: BoardDay[];
  departures: BoardDay[];
  missingArrival: string[];
  missingDeparture: string[];
}
export type AccStatus = 'requested' | 'arranged' | 'not_possible';
export interface Accommodation {
  id: string;
  kind: string;
  where: 'venue' | 'hotel';
  status: AccStatus;
  days?: string[];
  detail?: string | null;
  outcome?: string | null;
  inviteeId: string | null;
  name?: string;
}
export type Decision = 'pending' | 'approved' | 'approved_own_cost' | 'declined';
export interface PaymentRef {
  id: string;
  kind: string;
  status: string;
  route: string | null;
  amountCents: number | null;
}
export interface TravelException {
  id: string;
  kind: string;
  request?: string | null;
  decision: Decision;
  decidedOn?: string | null;
  decidedBy?: string | null;
  conditions?: string | null;
  paymentEntryId?: string | null;
  inviteeId: string;
  name: string;
  payment: PaymentRef | null;
  payments: PaymentRef[];
}
export interface Option {
  key: string;
  label: string;
}

export interface DayOf {
  days: { date: string; label: string | null }[];
  stations: Station[];
  groups: { names: string[]; sizes: { name: string; count: number }[]; assignedAt: string | null };
  garages: { key: string; name: string }[];
  counts: {
    shifts: number;
    staff: number;
    grouped: number;
    expected: number;
    parking: ParkingCounts;
    accommodations: Record<AccStatus, number>;
    exceptions: Record<Decision, number>;
    arrivals: number;
    departures: number;
    missingArrival: number;
  };
  kinds: { accommodations: Option[]; exceptions: Option[]; decisions: Option[] };
  visible: boolean;
  sessions?: DayOfSession[];
  shifts?: Shift[];
  people?: DayOfPerson[];
  parking?: { garages: Garage[]; codes: ParkingCode[] };
  travel?: Board;
  accommodations?: Accommodation[];
  exceptions?: TravelException[];
}

export interface Printable {
  title: string;
  html: string;
  text: string;
  count?: number;
}

export const useDayOf = (eventId: string) =>
  useQuery<DayOf>(['event-dayof', eventId], async () => (await api.get(`/events/${eventId}/dayof`)).data.data);

export function useDayOfWrite<T = unknown>(eventId: string, fn: (v: T) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation(fn, {
    onSuccess: () => {
      qc.invalidateQueries(['event-dayof', eventId]);
      qc.invalidateQueries(['event-dayof-today', eventId]);
      qc.invalidateQueries(['event-followups', eventId]);
    },
  });
}

/** Decisions are ordinary states: waiting is an open ring, approved a teal dot, declined a dash. */
export const DECISION_TONE: Record<Decision, 'healthy' | 'inactive' | 'running'> = {
  pending: 'running',
  approved: 'healthy',
  approved_own_cost: 'healthy',
  declined: 'inactive',
};
export const STATUS_LABEL: Record<AccStatus, string> = {
  requested: 'To arrange',
  arranged: 'Arranged',
  not_possible: 'Not possible',
};

// ---------------------------------------------------------------- blocks on the same tab

// Panels are listed one after another in plugins.ts and know nothing of each other. A panel that is on the page
// marks itself here while mounted, so a sibling can say a thing once (the tab's opening sentence, the viewer note)
// or fold two empty sections into one. Keys carry the event id.
const present = new Map<string, number>();
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};

/** Marks `key` as on the page while this component is mounted and `on` is true. */
export function useMarkPresent(key: string, on = true) {
  useLayoutEffect(() => {
    if (!on) return undefined;
    present.set(key, (present.get(key) ?? 0) + 1);
    emit();
    return () => {
      const n = (present.get(key) ?? 1) - 1;
      if (n > 0) present.set(key, n);
      else present.delete(key);
      emit();
    };
  }, [key, on]);
}

/** Is a block marked `key` on the page right now? */
export const usePresent = (key: string) =>
  useSyncExternalStore(
    subscribe,
    () => present.has(key),
    () => false,
  );

// ---------------------------------------------------------------- tables that stack on a phone

/** Table classes for a table that becomes one block per row below 768px: header hidden, each labelled cell on its
 *  own line prefixed with its column name (`data-label`), actions in a wrapping row at the end. */
export const stack = {
  table: 'w-full max-md:block',
  thead: 'max-md:hidden',
  tbody: 'max-md:block',
  tr: 'border-b border-line hover:bg-selected max-md:block max-md:py-3',
  td: 'max-md:block max-md:py-[2px] max-md:pr-0',
  labelled: "max-md:before:content-[attr(data-label)_'_·_'] max-md:before:text-small max-md:before:text-ink-3",
  acts: 'text-right whitespace-nowrap max-md:text-left max-md:whitespace-normal max-md:pt-2',
};
